import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { goBackFromPracticePage } from "../../custom_utilities/dashboardBannerNavigation";
import { useSelector } from "react-redux";
import images from "../../utilities/images/images";
import { fetchUserWrongTopics } from "./mistakeBookApi";
import { getMistakeBookSubjectsForCourse } from "./mistakeBookSubjects";
import { resolveSubjectIconComponent } from "./SubjectIcons";
import { ChartDownIcon, ChevronRightIcon } from "./WeakAreaIcons";
import topicMagnetIcon from "./assets/topic-magnet.png";
import topicElectroIcon from "./assets/topic-electro.png";
import topicCapacitorIcon from "./assets/topic-capacitor.png";
import noMistakesIllustrationMain from "./assets/no-mistakes-illustration-main.svg";
import noMistakesIllustrationBlobTop from "./assets/no-mistakes-illustration-blob-top.svg";
import noMistakesIllustrationBlobBottom from "./assets/no-mistakes-illustration-blob-bottom.svg";
import "./MistakeBookPage.css";

const DEFAULT_YEARS = [2026, 2025, 2024, 2023];
const TOPIC_ICON_PALETTE = [
  { bg: "#f2d8ea", icon: topicMagnetIcon },
  { bg: "#eedbce", icon: topicElectroIcon },
  { bg: "#e5e2f8", icon: topicCapacitorIcon },
  { bg: "#dbf2e3", icon: topicMagnetIcon },
];


const ProgressRing = ({ percent = 0 }) => {
  const size = 48;
  const stroke = 4;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div className="mb-progress-ring" aria-label={`${percent}% resolved`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          className="mb-progress-ring__track"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={stroke}
        />
        <circle
          className="mb-progress-ring__value"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <span className="mb-progress-ring__label">{percent}%</span>
    </div>
  );
};

const formatTopicMeta = (topic) => {
  const parts = [`${topic.mistakes} Mistakes`, `${topic.resolved} Resolved`];
  if (topic.newCount > 0) parts.push(`${topic.newCount} New`);
  return parts.join(" · ");
};

const SKELETON_TOPIC_COUNT = 4;

const MistakeBookSkeleton = () => (
  <div
    className="mistake-book-skeleton"
    role="status"
    aria-live="polite"
    aria-label="Loading mistake book"
  >
    <div className="mistake-book-skeleton__insight">
      <div className="mistake-book-skeleton__insight-left">
        <span className="mistake-book-skeleton__bone mistake-book-skeleton__insight-icon" />
        <div className="mistake-book-skeleton__insight-copy">
          <span className="mistake-book-skeleton__bone mistake-book-skeleton__insight-title" />
          <span className="mistake-book-skeleton__bone mistake-book-skeleton__insight-subtitle" />
        </div>
      </div>
      <span className="mistake-book-skeleton__bone mistake-book-skeleton__insight-chevron" />
    </div>

    <div className="mistake-book-topics">
      {Array.from({ length: SKELETON_TOPIC_COUNT }).map((_, index) => (
        <div className="mistake-book-skeleton__card" key={index}>
          <div className="mistake-book-skeleton__card-main">
            <span className="mistake-book-skeleton__bone mistake-book-skeleton__card-icon" />
            <div className="mistake-book-skeleton__card-text">
              <span className="mistake-book-skeleton__bone mistake-book-skeleton__card-title" />
              <span className="mistake-book-skeleton__bone mistake-book-skeleton__card-meta" />
            </div>
          </div>
          <span className="mistake-book-skeleton__bone mistake-book-skeleton__ring" />
        </div>
      ))}
    </div>
  </div>
);

const NoMistakesYetEmptyState = () => (
  <div className="mistake-book-empty-state" role="status">
    <div className="mistake-book-empty-state__illustration" aria-hidden="true">
      <img
        className="mistake-book-empty-state__blob mistake-book-empty-state__blob--top"
        src={noMistakesIllustrationBlobTop}
        alt=""
      />
      <img
        className="mistake-book-empty-state__main"
        src={noMistakesIllustrationMain}
        alt=""
      />
      <img
        className="mistake-book-empty-state__blob mistake-book-empty-state__blob--bottom"
        src={noMistakesIllustrationBlobBottom}
        alt=""
      />
    </div>
    <div className="mistake-book-empty-state__copy">
      <h2 className="mistake-book-empty-state__title">No Mistakes Yet</h2>
      <p className="mistake-book-empty-state__description">
        Mistakes from your tests and practice sessions will be added here
        automatically for revision.
      </p>
    </div>
  </div>
);

const MistakeBookPage = () => {
  const history = useHistory();
  const location = useLocation();
  const userPreference = useSelector((state) => state.userPreference);
  const targetId = userPreference?.targetDetails?.[0]?.target_id;
  const targetName = userPreference?.targetDetails?.[0]?.target ?? "";

  const subjects = useMemo(
    () => getMistakeBookSubjectsForCourse({ targetId, targetName }),
    [targetId, targetName]
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [year, setYear] = useState(DEFAULT_YEARS[0]);
  const [years] = useState(DEFAULT_YEARS);
  const [yearMenuOpen, setYearMenuOpen] = useState(false);
  const [activeSubjectId, setActiveSubjectId] = useState(subjects[0]?.id);
  const [topics, setTopics] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const yearDropdownRef = useRef(null);

  useEffect(() => {
    const stillExists = subjects.some(
      (subject) => String(subject.id) === String(activeSubjectId)
    );
    if (!stillExists) {
      setActiveSubjectId(subjects[0]?.id);
    }
  }, [subjects, activeSubjectId]);

  const activeSubject = useMemo(
    () => subjects.find((s) => String(s.id) === String(activeSubjectId)) || subjects[0],
    [subjects, activeSubjectId]
  );

  const loadMistakeBook = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const subjectName = activeSubject?.name || "Physics";
      const topicsPayload = await fetchUserWrongTopics({
        subject: subjectName,
      });

      setTopics(topicsPayload.topics || []);
      setAnalytics(
        topicsPayload.weakAnalysis
          ? {
              title: topicsPayload.weakAnalysis.title,
              subtitle: topicsPayload.weakAnalysis.subtitle,
              image: topicsPayload.weakAnalysis.image,
            }
          : null
      );
    } catch (err) {
      setTopics([]);
      setAnalytics(null);
      setError(
        err?.response?.data?.message ||
          "Unable to load Mistake Book right now. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [activeSubject?.name]);

  useEffect(() => {
    loadMistakeBook();
  }, [loadMistakeBook]);

  useEffect(() => {
    if (!yearMenuOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (
        yearDropdownRef.current &&
        !yearDropdownRef.current.contains(event.target)
      ) {
        setYearMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [yearMenuOpen]);

  const handleBack = () => {
    goBackFromPracticePage(history, location);
  };

  const handleYearSelect = (nextYear) => {
    setYear(Number(nextYear));
    setYearMenuOpen(false);
  };

  const handleBuildPracticeSets = () => {
    if (loading) return;

    if (!topics.length) {
      setPopupMessage(
        "Question Count should be more than 10"
      );
      setPopupOpen(true);
      return;
    }

    history.push("/practice/mistake-book/practice-sets", {
      year,
      subjectId: activeSubjectId,
      subjectName: activeSubject?.name,
      subject: activeSubject,
    });
  };

  return (
    <div className="mistake-book-page">
      <div className="mistake-book-sticky-top">
        <header className="mistake-book-header">
          <button
            type="button"
            className="mistake-book-header__back"
            onClick={handleBack}
            aria-label="Go back"
          >
            <img src={images.new_backarrow} alt="" />
          </button>
          <h1 className="mistake-book-header__title">Mistake Book</h1>
          <div className="mistake-book-header__year">
            <div
              className={`mistake-book-year-dropdown${
                yearMenuOpen ? " is-open" : ""
              }`}
              ref={yearDropdownRef}
            >
              <button
                type="button"
                className="mistake-book-year-dropdown__trigger"
                onClick={() => setYearMenuOpen((open) => !open)}
                aria-haspopup="listbox"
                aria-expanded={yearMenuOpen}
                aria-label="Select year"
              >
                <span>{year}</span>
                <svg
                  className="mistake-book-year-dropdown__chevron"
                  width="9"
                  height="6"
                  viewBox="0 0 8.701 5.156"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M8.45685 0.240019C8.3022 0.0862882 8.093 0 7.87494 0C7.65689 0 7.44769 0.0862882 7.29304 0.240019L4.32987 3.16192L1.40796 0.240019C1.25331 0.0862882 1.04411 0 0.826056 0C0.607998 0 0.398799 0.0862882 0.244151 0.240019C0.166788 0.316751 0.105383 0.40804 0.0634785 0.508622C0.0215742 0.609205 0 0.717089 0 0.826051C0 0.935013 0.0215742 1.0429 0.0634785 1.14348C0.105383 1.24406 0.166788 1.33535 0.244151 1.41208L3.74383 4.91177C3.82056 4.98913 3.91185 5.05053 4.01244 5.09244C4.11302 5.13434 4.2209 5.15592 4.32987 5.15592C4.43883 5.15592 4.54671 5.13434 4.64729 5.09244C4.74788 5.05053 4.83917 4.98913 4.9159 4.91177L8.45685 1.41208C8.53421 1.33535 8.59562 1.24406 8.63752 1.14348C8.67943 1.0429 8.701 0.935013 8.701 0.826051C8.701 0.717089 8.67943 0.609205 8.63752 0.508622C8.59562 0.40804 8.53421 0.316751 8.45685 0.240019Z"
                    fill="#334155"
                  />
                </svg>
              </button>
              {yearMenuOpen ? (
                <ul className="mistake-book-year-dropdown__menu" role="listbox">
                  {years.map((y) => (
                    <li key={y}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={Number(y) === Number(year)}
                        className={`mistake-book-year-dropdown__option${
                          Number(y) === Number(year) ? " is-selected" : ""
                        }`}
                        onClick={() => handleYearSelect(y)}
                      >
                        {y}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        </header>

        <nav className="mistake-book-subjects" aria-label="Subjects">
          {subjects.map((subject) => {
            const isActive = String(subject.id) === String(activeSubjectId);
            const SubjectIcon = resolveSubjectIconComponent(subject);
            return (
              <button
                key={subject.id}
                type="button"
                className={`mistake-book-subject-tab${
                  isActive ? " is-active" : ""
                }`}
                onClick={() => setActiveSubjectId(subject.id)}
              >
                <SubjectIcon className="mistake-book-subject-tab__icon" size={20} />
                <span>{subject.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      <div className="mistake-book-body">
        {loading ? (
          <MistakeBookSkeleton />
        ) : (
          <>
            {error ? <p className="mistake-book-error">{error}</p> : null}

            {topics.length ? (
              <>
                {analytics ? (
                  <button
                    type="button"
                    className="mistake-book-insight"
                    onClick={() =>
                      history.push("/practice/mistake-book/weak-area-analysis", {
                        year,
                        subjectId: activeSubjectId,
                        subjects,
                      })
                    }
                  >
                    <div className="mistake-book-insight__left">
                      <div className="mistake-book-insight__icon-wrap">
                        <ChartDownIcon
                          className="mistake-book-insight__chart-icon"
                          size={34}
                        />
                      </div>
                      <div className="mistake-book-insight__copy">
                        <div className="mistake-book-insight__title-row">
                          <span className="mistake-book-insight__badge">NEW</span>
                          <h2>{analytics.title}</h2>
                        </div>
                        <p>{analytics.subtitle}</p>
                      </div>
                    </div>
                    <ChevronRightIcon
                      className="mistake-book-insight__chevron"
                      size={28}
                    />
                  </button>
                ) : null}

                <div className="mistake-book-topics">
                  {topics.map((topic, index) => {
                    const palette =
                      TOPIC_ICON_PALETTE[index % TOPIC_ICON_PALETTE.length];
                    return (
                      <button
                        type="button"
                        key={topic.id}
                        className="mistake-book-topic-card"
                        onClick={() =>
                          history.push("/practice/mistake-book/topic-questions", {
                            topic,
                            topicName: topic.name,
                            subject: activeSubject,
                            subjectName: activeSubject?.name,
                            subjectId: activeSubjectId,
                            year,
                            fromMistakeBook: true,
                          })
                        }
                      >
                        <div className="mistake-book-topic-card__main">
                          <div
                            className="mistake-book-topic-card__icon"
                            style={{ background: palette.bg }}
                          >
                            <img src={topic.icon || palette.icon} alt="" />
                          </div>
                          <div className="mistake-book-topic-card__text">
                            <h3>{topic.name}</h3>
                            <p>{formatTopicMeta(topic)}</p>
                          </div>
                        </div>
                        <ProgressRing percent={topic.percent} />
                      </button>
                    );
                  })}
                </div>
              </>
            ) : !error ? (
              <NoMistakesYetEmptyState />
            ) : null}
          </>
        )}
      </div>

      <div className="mistake-book-cta">
        <div className="mistake-book-cta__inner">
          <div className="mistake-book-cta__copy">
            <h2>Build Practice Sets from your mistakes</h2>
            <p>
              Auto-mix questions across selected topics to drill what matters
              most.
            </p>
          </div>
          <button
            type="button"
            className="mistake-book-cta__button"
            onClick={handleBuildPracticeSets}
            disabled={loading}
          >
            Build Practice Sets
          </button>
        </div>
      </div>

      {popupOpen ? (
        <div
          className="mistake-book-popup-overlay"
          role="presentation"
          onClick={() => setPopupOpen(false)}
        >
          <div
            className="mistake-book-popup"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="mistake-book-popup-title"
            aria-describedby="mistake-book-popup-message"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="mistake-book-popup-title" className="mistake-book-popup__title">
              Unable to build practice set
            </h3>
            <p id="mistake-book-popup-message" className="mistake-book-popup__message">
              {popupMessage}
            </p>
            <button
              type="button"
              className="mistake-book-popup__button"
              onClick={() => setPopupOpen(false)}
            >
              Okay
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default MistakeBookPage;
